import { palette } from '@theme/palette';
import type { ThemeConfig } from 'antd-v6';
import { theme } from 'antd-v6';

export type Antd6Mode = 'dark' | 'light';

type ComponentsConfig = NonNullable<ThemeConfig['components']>;
type ThemeSlice = Pick<ThemeConfig, 'token' | 'components'>;

// Off-palette; kept to preserve the current dark Select rendering.
const SELECT_DARK_BG = '#2C2F38';
const SELECT_DARK_BORDER = '#3A3E48';

const sharedComponents: ComponentsConfig = {
  Segmented: {
    itemSelectedBg: palette.blue[60],
    itemSelectedColor: palette.white,
    itemActiveBg: palette.blue[60],
    itemColor: palette.neutral[30],
    itemHoverBg: palette.white,
    itemHoverColor: palette.blue[60],
  },
  Tooltip: {
    colorBgSpotlight: palette.white,
    colorTextLightSolid: palette.neutral[100],
  },
};

const darkComponents: ComponentsConfig = {
  Select: {
    colorBgContainer: SELECT_DARK_BG,
    colorBorder: SELECT_DARK_BORDER,
    colorText: palette.white,
    colorTextQuaternary: palette.neutral[30],
    colorPrimary: palette.blue[60],
    activeBorderColor: palette.blue[60],
    controlHeightLG: 32,
    borderRadiusLG: 8,
    colorBgElevated: SELECT_DARK_BG,
    colorTextBase: palette.white,
    optionActiveBg: SELECT_DARK_BORDER,
    optionSelectedBg: palette.blue[60],
    optionSelectedColor: palette.white,
    colorIcon: palette.neutral[30],
    colorIconHover: palette.white,
    paddingXXS: 0,
    borderRadiusSM: 0,
  },
  Radio: {
    // Unchecked: dark fill + muted grey ring
    colorBgContainer: palette.neutral[100],
    colorBorder: palette.neutral[70],
    dotSize: 8,
    radioColor: palette.blue[60], // the dot
    radioBgColor: palette.neutral[100], // the gap behind the dot
  } as ComponentsConfig['Radio'],
  Popover: {
    colorBgElevated: palette.neutral[90],
    colorText: palette.white,
  },
  Button: {
    colorFillTertiary: palette.neutral[80],
    sizeSM: 18,
  },
};

const lightComponents: ComponentsConfig = {
  Select: {
    colorBgContainer: palette.neutral[10],
    colorBorder: palette.neutral[30],
    colorText: palette.neutral[100],
    colorTextQuaternary: palette.neutral[60],
    colorPrimary: palette.blue[60],
    activeBorderColor: palette.blue[60],
    controlHeightLG: 32,
    borderRadiusLG: 8,
    colorBgElevated: palette.white,
    colorTextBase: palette.neutral[100],
    optionActiveBg: palette.neutral[10],
    optionSelectedBg: '#E8F4FF',
    optionSelectedColor: palette.blue[60],
    colorIcon: palette.neutral[60],
    colorIconHover: palette.neutral[100],
  },
  DatePicker: {
    colorBgContainer: '#F5F5F5',
    colorBorder: palette.neutral[30],
    activeBorderColor: palette.blue[60],
    hoverBorderColor: palette.blue[60],
    borderRadiusLG: 4,
    controlHeightLG: 40,
  },
  Popover: {
    colorBgElevated: palette.white,
    colorText: palette.neutral[100],
  },
  Segmented: {
    trackBg: palette.neutral[20],
    itemColor: palette.neutral[100],
    itemHoverColor: palette.neutral[100],
    itemHoverBg: 'rgba(255, 255, 255, 0.5)',
    itemActiveBg: palette.white,
    itemSelectedBg: palette.white,
    itemSelectedColor: palette.neutral[100],
  },
  Slider: {
    trackBg: palette.blue[50],
    trackHoverBg: palette.blue[50],
    railBg: palette.neutral[20],
    railHoverBg: palette.neutral[20],
    handleColor: palette.blue[50],
    handleActiveColor: palette.blue[50],
  },
};

const CSS_VAR_KEY: Record<Antd6Mode, string> = { dark: 'antd6-dark', light: 'antd6-light' };

export const antd6DarkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  cssVar: { key: CSS_VAR_KEY.dark },
  components: { ...sharedComponents, ...darkComponents },
  token: {
    colorText: palette.white,
    colorPrimary: palette.blue[60],
  },
};

export const antd6LightTheme: ThemeConfig = {
  cssVar: { key: CSS_VAR_KEY.light },
  components: { ...sharedComponents, ...lightComponents },
};

/** Opt-in theme slices, validated against ThemeSlice where they are consumed below. */
export const antd6Variants = {
  brandPrimaryButton: {
    components: {
      Button: {
        colorPrimary: palette.green[60],
        colorPrimaryHover: palette.green[50],
        colorPrimaryActive: palette.green[70],
      },
    },
  },
  // Green is reserved for the primary action, so default buttons keep a neutral hover/active.
  neutralDefaultButton: {
    components: {
      Button: {
        defaultHoverColor: palette.neutral[100],
        defaultHoverBorderColor: palette.neutral[100],
        defaultActiveColor: palette.neutral[100],
        defaultActiveBorderColor: palette.neutral[100],
      },
    },
  },
  // The design's neutral chip: a flat neutral-10 fill and no border, via `variant="filled"`.
  neutralFilledButton: {
    components: {
      Button: {
        colorFillTertiary: palette.neutral[10],
        colorFillSecondary: palette.neutral[20],
        colorFill: palette.neutral[30],
      },
    },
  },
  darkTooltip: {
    components: {
      Tooltip: { colorBgSpotlight: palette.neutral[90], colorTextLightSolid: palette.white },
    },
  },
  aiHighlightPopover: {
    components: { Popover: { colorBgElevated: '#E5D7FA' } },
  },
  navySegmented: {
    components: {
      Segmented: {
        itemColor: palette.neutral[30],
        itemHoverColor: palette.white,
        itemHoverBg: 'rgba(255, 255, 255, 0.08)',
        itemActiveBg: palette.blue[80],
        itemSelectedBg: palette.blue[80],
        itemSelectedColor: palette.white,
      },
    },
  },
  filterDatePicker: {
    components: {
      DatePicker: {
        // Pinned against the light base so the pill trigger keeps its white fill and 8px radius.
        colorBgContainer: palette.white,
        borderRadiusLG: 8,
        paddingInline: 22.5,
        // 7 + 24 (icon/line box) + 7 + 2 borders = the 40px control height.
        paddingBlock: 7,
        hoverBg: palette.neutral[10],
        hoverBorderColor: palette.neutral[20],
        activeBg: palette.white,
        // The focus ring is an offset outline, not a border swap, so the border stays neutral.
        activeBorderColor: palette.neutral[20],
        activeShadow: 'none',
        cellWidth: 36,
        cellHoverBg: palette.neutral[10],
        // Out-of-range days are grey text only; the default fill bands whole rows.
        cellBgDisabled: 'transparent',
      },
    },
  },
  roundedDatePicker: {
    components: {
      DatePicker: {
        borderRadiusLG: 8,
        colorBgContainer: palette.neutral[10],
        colorBorder: palette.neutral[20],
      },
    },
  },
  filterSearchInput: {
    components: {
      Input: {
        borderRadius: 28,
        borderRadiusSM: 28,
        colorBgContainer: palette.neutral[10],
        // antd derives these from colorBgContainer, so pin them or hover/focus flash white.
        hoverBg: palette.neutral[10],
        activeBg: palette.neutral[10],
        colorText: palette.neutral[60],
        colorTextPlaceholder: palette.neutral[60],
        inputFontSizeSM: 14,
      },
    },
  },
  pillDropdownMenu: {
    components: {
      Dropdown: {
        borderRadiusLG: 12,
        boxShadowSecondary: '0 2px 8px 0 rgba(0, 0, 0, 0.15)',
        paddingXXS: 8,
        borderRadiusSM: 8,
        paddingBlock: 4,
        controlPaddingHorizontal: 12,
        lineHeight: 20 / 14,
        colorText: palette.neutral[80],
        colorPrimary: palette.neutral[100],
        controlItemBgHover: palette.neutral[10],
        controlItemBgActive: palette.blue[10],
        controlItemBgActiveHover: palette.blue[10],
      },
    },
  },
  // Layered over pillDropdownMenu; marginXS only insets the item-group list here.
  indexSelectMenu: {
    components: {
      Dropdown: {
        borderRadiusLG: 8,
        paddingXXS: 4,
        paddingBlock: 4,
        marginXS: 0,
        colorTextDescription: palette.neutral[60],
      },
    },
  },
  yieldListSortMenu: {
    components: {
      Dropdown: {
        controlItemBgActive: palette.blue[90],
        controlItemBgActiveHover: palette.blue[90],
        controlItemBgHover: palette.neutral[90],
        colorPrimary: palette.white,
      },
    },
  },
  dashboardExport: {
    components: {
      Button: { contentFontSizeLG: 14 },
      Dropdown: { paddingXXS: 4, paddingBlock: 6, colorText: palette.neutral[100] },
    },
  },
  reportModal: {
    // The only masked overlay in the dashboard scope, so a global mask token is safe here.
    token: { colorBgMask: 'rgba(20, 21, 28, 0.6)' },
    components: {
      Modal: { borderRadiusLG: 16, boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.15)' },
      // Scoped, not global: the other two antd6 Progress bars mean loading, not brand action.
      Progress: {
        defaultColor: palette.green[60],
        remainingColor: palette.neutral[20],
        lineBorderRadius: 100,
      },
    },
  },
  roundedModal: {
    components: {
      Modal: {
        borderRadiusLG: 24,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
        titleColor: palette.neutral[100],
        titleFontSize: 16,
        titleLineHeight: 1.5,
      },
      InputNumber: {
        borderRadius: 8,
        colorBorder: palette.neutral[30],
        controlWidth: 60,
      },
      Slider: { handleSize: 12, handleSizeHover: 12 },
    },
  },
  // antd's stock colorPrimary is #1677ff; the design system's interactive accent is blue-60.
  interactivePrimary: {
    token: { colorPrimary: palette.blue[60] },
  },
  compactRadius: {
    token: { borderRadius: 4 },
  },
} as const;

/** antd has no letter-spacing token, so `pillDropdownMenu` menus carry it as a class. */
export const antd6DropdownMenuClass = 'tracking-[-0.105px]';

export type Antd6VariantName = keyof typeof antd6Variants;

/**
 * Merges variants over a base, per component token (antd's own semantics) rather than replacing
 * whole component entries. The cssVar key is derived from the inputs so that two providers share
 * a CSS scope only when they resolve to the same tokens.
 */
export function resolveAntd6Theme(
  mode: Antd6Mode,
  variants: readonly Antd6VariantName[]
): ThemeConfig {
  const base = mode === 'light' ? antd6LightTheme : antd6DarkTheme;
  if (variants.length === 0) return base;

  let token = base.token;
  const components: Record<string, object> = { ...base.components };

  for (const name of variants) {
    const slice: ThemeSlice = antd6Variants[name];
    token = { ...token, ...slice.token };
    for (const [component, tokens] of Object.entries(slice.components ?? {})) {
      components[component] = { ...components[component], ...tokens };
    }
  }

  return {
    ...base,
    token,
    components: components as ComponentsConfig,
    cssVar: { key: [CSS_VAR_KEY[mode], ...variants].join('-') },
  };
}
